import React, { useCallback, useEffect, useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import cn from '~/Utils/cn';

import { COVERAGE_CONFIGURATION_LIMITS } from '../../../../Types';
import { isClaimPolicyManuallyFilled, reportAxiosError } from '../../../../Utils';
import { getDeductibleText } from '../../../../Utils/deductibleUtils';
import CardDialog from '../../../CardDialog';
import { useClaim } from '../../../ClaimContainer';
import WithConfirm from '../../../ConfirmModal';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '../../../core';
import CoveredEntityCoveragesTable from '../../../CoveredEntity/CoveredEntityCoveragesTable';
import useCurrencyFormatter from '../../../CurrencyFormatterContext';
import { PencilIcon, TrashIcon } from '../../../icons';
import { CustomField } from '../../../IncidentConfiguration/ConfiguredFields';
import { CustomFieldsContextProvider } from '../../../IncidentConfiguration/CustomFieldsContext';
import InlineIconButton from '../../../InlineIconButton';
import useOrganization from '../../../OrganizationContext';
import { ShowOnlyTextField } from '../../../TextFieldFormik';

import EditCoveredEntityDialog from './EditCoveredEntityDialog/EditCoveredEntityDialog';
import CoveredEntityAccumulatedFinancialContainer from './CoveredEntityAccumulatedFinance';

import { useStyles } from '../../../../assets/styles';
import styles from './CoveredEntityCard.module.scss';

// Viewport height minus an approximation of the CardDialog chrome (title bar +
// header padding + the MUI Dialog's top/bottom viewport margins), used to cap
// the split-layout body so it scrolls as a single scrollbar — the surrounding
// Card/Dialog DOM isn't a height-constrained flex chain, so it would otherwise
// swallow the overflow and clip the content. The deduction is intentionally
// generous: erring toward a little dead space beats clipping the last rows when
// a long covered-object title wraps. (A flex `min-h-0` chain would be exact but
// requires the shared CardDialog/Dialog ancestors to opt into it.)
const SPLIT_LAYOUT_BODY_MAX_HEIGHT = 'calc(100vh - 220px)';

function CoveredEntityCard(props) {
  const {
    icon,
    coveredObjectTypeLabel,
    defaultFields,
    configuredFields,
    coveredObjectIdForPatchEndpoints,
    displayFieldsCount = 4,
    fetchCoveredEntityEndpoint,
    getPatchCoveredEntityEndpoint,
    getPatchCoveragesEndpoint,
    getPatchSharedCoveragesEndpoint,
    coveredObject,
    isFirstParty = false,
    // V2-only options (V1 callers omit and get the existing behavior):
    // hideHeader: skip the card's title bar so the host (e.g. V2 view dialog)
    //   can render title + action in its own header (the outer modal title).
    // coveragesDefaultOpen: open the Coverages accordion on first render.
    // onCoveredEntityLoaded: invoked after fetchCoveredEntity succeeds so the
    //   host can mount edit dialogs that need the per-entity data.
    // refreshKey: bumping this triggers a re-fetch of the per-entity data,
    //   used after the host edits via an external EditCoveredEntityDialog.
    hideHeader = false,
    coveragesDefaultOpen = false,
    // splitLayout: render the details grid (left) and the Coverages block
    //   (right) side by side instead of stacked. Used by the V2 full-screen
    //   view dialog. showAllFields: render every field with no Show More/Less
    //   toggle. Both default off so V1's inline card is unchanged.
    splitLayout = false,
    showAllFields = false,
    onCoveredEntityLoaded,
    refreshKey,
  } = props;
  const { claim, onAsyncClaimUpdate } = useClaim();
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [showEditCoveredEntityModal, setShowEditCoveredEntityModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { coverageConfiguration } = useOrganization();
  const { currencyFormatter } = useCurrencyFormatter();
  const isManualPolicy = isClaimPolicyManuallyFilled(claim);
  const isAutoClaim = claim?.lob === 'auto_claim';
  const isPetClaim = claim?.lob === 'pet_claim';
  // In the split layout the details grid lives in a ~5/12 column, so drop to
  // 2-up instead of the 3/4-up used when it spans the full width.
  const columnsClassName = splitLayout ? 'grid-cols-2' : isAutoClaim ? 'grid-cols-4' : 'grid-cols-3';
  const wasUnlisted = coveredObject?.was_unlisted || false;
  // A claim-owned object stays claim-owned after promotion — removing it from the policy
  // has to return it to unlisted so its coverages and exposures survive.
  const isClaimOwned = coveredObject?.is_claim_owned || false;
  const policyPrefix = `${isManualPolicy ? '' : 'api_'}`;
  const coveredVehicleDisplayName = coveredObject?.vehicle?.display_name;
  const coveredObjectDisplayName = coveredObject?.display_name || coveredObject?.contact?.full_name;
  const cardHeaderTitle =
    (isAutoClaim ? coveredVehicleDisplayName : coveredObjectDisplayName) || coveredObjectTypeLabel;
  const [isLoadingCoveredEntity, setIsLoadingCoveredEntity] = useState(true);
  const [coveredEntity, setCoveredEntity] = useState();

  const fetchCoveredEntity = useCallback(async () => {
    try {
      const res = await axios.get(fetchCoveredEntityEndpoint);
      setCoveredEntity(res.data);
      setIsLoadingCoveredEntity(false);
      onCoveredEntityLoaded?.(res.data);
    } catch (error) {
      await reportAxiosError(error);
    }
  }, [fetchCoveredEntityEndpoint, onCoveredEntityLoaded]);

  useEffect(() => {
    fetchCoveredEntity();
    // refreshKey is a deliberate dep: bumping it re-runs the fetch so callers
    // that edit via an external dialog (V2 view dialog) can refresh the data.
  }, [fetchCoveredEntity, refreshKey]);

  const coveredEntityTitle = (
    <span className={styles.cardTitleWrapper}>
      {icon}
      <span className={styles.cardTitleWrapperLabel}>{cardHeaderTitle}</span>
      {isFirstParty && (
        <span className="ml-12">
          <Chip size="small" label="First Party Involved" className={`${classes.chip} font-normal`} />
        </span>
      )}
    </span>
  );

  async function handleCoveredEntityUpdate(values) {
    setIsSaving(true);
    try {
      const url = getPatchCoveredEntityEndpoint
        ? getPatchCoveredEntityEndpoint()
        : `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${coveredObjectIdForPatchEndpoints}`;
      await axios.patch(url, values);
      await onAsyncClaimUpdate();
      await fetchCoveredEntity();
      setShowEditCoveredEntityModal(false);
    } catch (error) {
      reportAxiosError(error);
      setIsSaving(false);
      throw error;
    }
    setIsSaving(false);
  }

  async function handleUnlistCoveredVehicle() {
    setIsSaving(true);
    try {
      const url = `/api/v1/auto_claims/${claim.id}/${policyPrefix}policy/covered_vehicles/${coveredObject.id}/set_unlisted`;
      await axios.patch(url);
      await onAsyncClaimUpdate();
    } catch (error) {
      reportAxiosError(error);
    }
    setIsSaving(false);
  }

  async function handleUnlistCoveredObject() {
    setIsSaving(true);
    try {
      const url = `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${coveredObject.id}/set_unlisted`;
      await axios.patch(url);
      await onAsyncClaimUpdate();
    } catch (error) {
      reportAxiosError(error);
    }
    setIsSaving(false);
  }

  async function handleRemoveCoveredObject() {
    setIsSaving(true);
    try {
      const url = `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${coveredObject.id}`;
      await axios.delete(url);
      await onAsyncClaimUpdate();
    } catch (error) {
      reportAxiosError(error);
    }
    setIsSaving(false);
  }

  const coveredEntityAction = (
    <div className={styles.actionsContainer}>
      <RestrictedPermissions
        action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
        verb={PERMISSION_VERBS.WRITE}
      >
        <div className={styles.editIconWrapper}>
          <InlineIconButton
            icon={PencilIcon}
            className={styles.editIcon}
            onClick={() => setShowEditCoveredEntityModal(true)}
            tooltipTitle={coveredEntity ? `Edit ${coveredObjectTypeLabel}` : `Loading ${coveredObjectTypeLabel}...`}
            disabled={!coveredEntity}
          />
          {coveredObject && (
            <WithConfirm
              title={`Remove this ${coveredObjectTypeLabel}?`}
              contentText={`Are you sure you want to remove this ${coveredObjectTypeLabel} from the policy?`}
              primaryButtonName="Yes, Remove"
              shouldCloseOnPrimary
            >
              <InlineIconButton
                icon={TrashIcon}
                className={styles.deleteIcon}
                onClick={
                  wasUnlisted
                    ? handleUnlistCoveredVehicle
                    : isClaimOwned
                      ? handleUnlistCoveredObject
                      : handleRemoveCoveredObject
                }
                tooltipTitle={`Remove ${coveredObjectTypeLabel} from policy`}
              />
            </WithConfirm>
          )}
        </div>
      </RestrictedPermissions>
    </div>
  );

  let accumulatedEntityFields = null;
  if (coveredEntity) {
    accumulatedEntityFields = (
      <>
        {isPetClaim && (
          <ShowOnlyTextField
            classes={classes}
            showOnlyValueComponent={currencyFormatter.format(coveredEntity.deductible_per_policy_per_covered_entity)}
            label={`${getDeductibleText()} per Listed Entity (per Policy)`}
            showOnly
            fullWidth
            className={classes.textField}
          />
        )}
        <ShowOnlyTextField
          classes={classes}
          showOnlyValueComponent={
            coveredEntity.limit_per_policy_per_covered_entity !== null &&
            coverageConfiguration.limit_per_policy_per_covered_entity &&
            currencyFormatter.format(coveredEntity.limit_per_policy_per_covered_entity)
          }
          label={COVERAGE_CONFIGURATION_LIMITS.limit_per_policy_per_covered_entity.desc}
          showOnly
          disabled={!coverageConfiguration.limit_per_policy_per_covered_entity}
          fullWidth
          className={classes.textField}
        />
        <ShowOnlyTextField
          classes={classes}
          showOnlyValueComponent={
            coveredEntity.limit_per_covered_entity !== null &&
            coverageConfiguration.limit_per_covered_entity &&
            currencyFormatter.format(coveredEntity.limit_per_covered_entity)
          }
          label={COVERAGE_CONFIGURATION_LIMITS.limit_per_covered_entity.desc}
          showOnly
          disabled={!coverageConfiguration.limit_per_covered_entity}
          fullWidth
          className={classes.textField}
        />
      </>
    );
  }

  const allFieldConfigurations = defaultFields.concat(configuredFields);

  const allFields = allFieldConfigurations
    .map((field) => (
      <CustomField
        key={field.id}
        field={field}
        readOnly
        disabled
        value={field.value}
        inline={false}
        idPrefix=""
        maxWidth="100%"
        overFlowWithTooltip={true}
      />
    ))
    .concat(accumulatedEntityFields)
    .filter(Boolean);

  // When showAllFields is set (split / full-screen view) there is no toggle:
  // every field is rendered and hiddenFields stays empty so the Show More/Less
  // blocks below are skipped.
  const showFields = showAllFields ? allFields : allFields.filter((field, i) => i < displayFieldsCount);
  const hiddenFields = showAllFields ? [] : allFields.filter((field, i) => i >= displayFieldsCount);

  const toggleAllFieldsButton =
    hiddenFields.length > 0 ? (
      <div className={cn(styles.showAll, 'col-span-full')}>
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
        >
          {`Show ${expanded ? 'Less' : 'All'}`}
        </Button>
      </div>
    ) : null;

  const fieldsBlock = (
    <CustomFieldsContextProvider customFieldsConfigurations={allFieldConfigurations}>
      <div className={cn('grid gap-x-12 gap-y-32', columnsClassName, { 'mb-32': !toggleAllFieldsButton })}>
        {showFields}
        {expanded && hiddenFields}
        {toggleAllFieldsButton}
      </div>
    </CustomFieldsContextProvider>
  );

  const coveragesBlock = (
    <>
      <RestrictedPermissions
        action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
        verb={PERMISSION_VERBS.WRITE}
      >
        <CoveredEntityCoveragesTable
          coveredEntity={coveredEntity}
          isLoadingCoveredEntity={isLoadingCoveredEntity}
          defaultOpen={coveragesDefaultOpen}
          onCoverageUpdate={async (postValues, coverageKey) => {
            const url = getPatchCoveragesEndpoint
              ? getPatchCoveragesEndpoint(coverageKey)
              : `/api/v1/claims/${claim.id}/${
                  isManualPolicy ? '' : 'api_'
                }policy/covered_objects/${coveredObjectIdForPatchEndpoints}/coverage/${coverageKey}`;
            await axios.patch(url, postValues);
            await fetchCoveredEntity();
          }}
          onSharedCoverageUpdate={async (postValues, sharedCoverageKey) => {
            const url = getPatchSharedCoveragesEndpoint
              ? getPatchSharedCoveragesEndpoint(sharedCoverageKey)
              : `/api/v1/claims/${claim.id}/${
                  isManualPolicy ? '' : 'api_'
                }policy/covered_objects/${coveredObjectIdForPatchEndpoints}/shared_coverage/${sharedCoverageKey}`;
            await axios.patch(url, postValues);
            await fetchCoveredEntity();
          }}
        />
      </RestrictedPermissions>
      {isPetClaim && coveredEntity && coveredEntity.entity_id && (
        <CoveredEntityAccumulatedFinancialContainer coveredEntity={coveredEntity} />
      )}
    </>
  );

  return (
    <CardDialog
      title={hideHeader ? undefined : coveredEntityTitle}
      action={hideHeader ? undefined : coveredEntityAction}
      noCardTitle={hideHeader}
      headerStyle={{ padding: '0 0 24px' }}
      containerStyle={{ padding: '24px', marginBottom: '24px' }}
      contentStyle={{ padding: '0' }}
    >
      {splitLayout ? (
        // One scroll container around both columns (see SPLIT_LAYOUT_BODY_MAX_HEIGHT)
        // so the whole body scrolls as a single scrollbar.
        <div className="overflow-y-auto pr-8" style={{ maxHeight: SPLIT_LAYOUT_BODY_MAX_HEIGHT }}>
          <div className="flex items-start gap-24">
            <div className="w-5/12 min-w-0">{fieldsBlock}</div>
            <div className="w-7/12 min-w-0">{coveragesBlock}</div>
          </div>
        </div>
      ) : (
        <>
          {fieldsBlock}
          <div className={styles.separator} />
          {coveragesBlock}
        </>
      )}
      {showEditCoveredEntityModal && (
        <EditCoveredEntityDialog
          icon={icon}
          coveredObjectTypeLabel={coveredObjectTypeLabel}
          defaultFields={defaultFields}
          configuredFields={configuredFields}
          coveredEntity={coveredEntity}
          onSaveClick={(values) => handleCoveredEntityUpdate(values)}
          onClose={() => !isSaving && setShowEditCoveredEntityModal(false)}
          isSaving={isSaving}
          isUpdate
        />
      )}
    </CardDialog>
  );
}

CoveredEntityCard.propTypes = {
  icon: PropTypes.object.isRequired,
  coveredObjectTypeLabel: PropTypes.string.isRequired,
  defaultFields: PropTypes.array.isRequired,
  configuredFields: PropTypes.array.isRequired,
  displayFieldsCount: PropTypes.number,
  fetchCoveredEntityEndpoint: PropTypes.string.isRequired,
  coveredObjectIdForPatchEndpoints: requiredIf(
    PropTypes.string,
    (props) =>
      !props.getPatchCoveredEntityEndpoint || !props.getPatchCoveragesEndpoint || !props.getPatchSharedCoveragesEndpoint
  ),
  getPatchCoveredEntityEndpoint: PropTypes.func,
  getPatchCoveragesEndpoint: PropTypes.func,
  getPatchSharedCoveragesEndpoint: PropTypes.func,
  coveredObject: PropTypes.object,
  isFirstParty: PropTypes.bool,
  hideHeader: PropTypes.bool,
  coveragesDefaultOpen: PropTypes.bool,
  splitLayout: PropTypes.bool,
  showAllFields: PropTypes.bool,
  onCoveredEntityLoaded: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default CoveredEntityCard;
